import { useCallback, useEffect, useMemo, useState } from "react";
import { aiAuthFetch } from "./aiAuthFetch";
import { garmentByKey, SIZE_RUNS } from "../Data/uniformCatalog";
import { FIRM } from "./campus_invoice";
import logoUrl from "../assets/bill/invoice-logo.svg";
import signUrl from "../assets/bill/sign.png";
import "./campus_orders.css";
import "./uniform_orders.css";

// Uniform orders: one organisation, one bill, many people. Staff (MUSTAQBIL)
// bring the people in — items, fabric, size or measurements. Here the admin
// sets rates, bills members (a new bill, or late joiners added to an old one),
// records payments and marks cutting started. Server: MaviinciAI
// app/services/uniform_store.py — bill numbers and totals are worked out
// there, never here.
const API = "https://maviinci-ai-bluepearl-e9750.web.app/api";
const HSN_GARMENT = "6211";

async function call(path, { method = "GET", body } = {}) {
    const res = await aiAuthFetch(`${API}${path}`, {
        method,
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `Server error ${res.status}`);
    return data;
}

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmtINR = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const abs = (u) => new URL(u, window.location.origin).href;
const label = (key) => garmentByKey(key)?.label || String(key || "").replace(/_/g, " ");
const runOf = (key) => {
    const z = garmentByKey(key)?.sizeRun;
    return z === "waist" ? "waist" : z === "free" ? "free" : "tops";
};
const sizeOrder = (key) => SIZE_RUNS[runOf(key)] || [];

const STATES = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana",
    "Himachal Pradesh", "Jammu & Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Madhya Pradesh", "Maharashtra",
    "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim",
    "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

const memberItemsText = (m) => {
    const rows = {};
    for (const it of m.active_items || []) {
        const size = m[`${it.id}_size`] || "";
        const k = `${it.type}|${size}`;
        rows[k] = rows[k] || { type: it.type, size, n: 0 };
        rows[k].n++;
    }
    return Object.values(rows).map((r) => `${label(r.type)}${r.size ? ` ${r.size}` : ""} × ${r.n}`).join(", ");
};

// ── page ────────────────────────────────────────────────────────────────────

export default function UniformOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError("");
        try { setOrders((await call("/uniform/orders")).orders || []); }
        catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (openId) return <OrderDetail orderId={openId} onBack={() => { setOpenId(null); load(); }} />;

    const total = orders.reduce((a, o) => a + (o.counts?.members || 0), 0);
    const unbilled = orders.reduce((a, o) => a + (o.counts?.unbilled || 0), 0);
    const flagged = orders.reduce((a, o) => a + (o.counts?.flagged || 0), 0);
    return (
        <div className="campus-orders-page uni-admin">
            <div className="uni-bar">
                <div>
                    <h3 className="uni-h">Uniform orders</h3>
                    <p className="campus-subnote">Staff add organisations and people from the Mustaqbil app. Rates, bills and payments are set here.</p>
                </div>
                <button type="button" className="campus-orders-refresh" onClick={load} disabled={loading}>{loading ? "Loading…" : "↻ Refresh"}</button>
            </div>
            {error && <div className="campus-orders-error">{error}</div>}
            <div className="campus-stat-row">
                <div className="campus-stat"><span className="campus-stat-label">Orders</span><span className="campus-stat-value">{orders.length}</span></div>
                <div className="campus-stat"><span className="campus-stat-label">People</span><span className="campus-stat-value">{total}</span></div>
                <div className={`campus-stat ${unbilled ? "" : "campus-stat--good"}`}><span className="campus-stat-label">Not billed</span><span className="campus-stat-value">{unbilled}</span></div>
                <div className={`campus-stat ${flagged ? "campus-stat--bad" : ""}`}><span className="campus-stat-label">Changed after billing / cutting</span><span className="campus-stat-value">{flagged}</span></div>
            </div>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr>
                        <th>Order</th><th>Organisation</th><th>Name</th><th className="c">People</th><th className="c">Done</th>
                        <th className="c">Not billed</th><th className="c">Flagged</th><th>Cutting</th><th>Created</th>
                    </tr></thead>
                    <tbody>
                        {orders.length === 0 && <tr><td colSpan={9} className="c">{loading ? "Loading…" : "No uniform orders yet."}</td></tr>}
                        {orders.map((o) => {
                            const c = o.counts || {};
                            return (
                                <tr key={o.id} className="uni-click" onClick={() => setOpenId(o.id)}>
                                    <td><b>{o.order_no}</b></td>
                                    <td>{o.org_name}</td>
                                    <td>{o.title || "—"}</td>
                                    <td className="c">{c.members || 0}</td>
                                    <td className="c">{c.done || 0}</td>
                                    <td className="c">{c.unbilled ? <span className="uni-pill warn">{c.unbilled}</span> : "0"}</td>
                                    <td className="c">{c.flagged ? <span className="uni-pill bad">{c.flagged}</span> : "0"}</td>
                                    <td>{o.production_started ? `Started ${fmtDate(o.production_started_at)}` : "—"}</td>
                                    <td>{fmtDate(o.created_at)}{o.created_by_name ? ` · ${o.created_by_name}` : ""}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── one order ───────────────────────────────────────────────────────────────

const TABS = [
    { id: "members", label: "People" },
    { id: "summary", label: "Size & fabric summary" },
    { id: "bills", label: "Rates & bills" },
    { id: "org", label: "Organisation" },
];

function OrderDetail({ orderId, onBack }) {
    const [order, setOrder] = useState(null);
    const [members, setMembers] = useState([]);
    const [bills, setBills] = useState([]);
    const [orgs, setOrgs] = useState([]);
    const [tab, setTab] = useState("members");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const load = useCallback(async () => {
        setBusy(true); setError("");
        try {
            const [d, b, g] = await Promise.all([
                call(`/uniform/orders/${orderId}`),
                call(`/uniform/orders/${orderId}/bills`),
                call("/uniform/orgs"),
            ]);
            setOrder(d.order); setMembers(d.members || []); setBills(b.bills || []); setOrgs(g.orgs || []);
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    }, [orderId]);
    useEffect(() => { load(); }, [load]);

    const run = async (fn, ok) => {
        setBusy(true); setError(""); setNotice("");
        try { await fn(); if (ok) setNotice(ok); await load(); return true; }
        catch (e) { setError(e.message); setBusy(false); return false; }
    };

    if (!order) {
        return (
            <div className="campus-orders-page uni-admin">
                <button type="button" className="uni-back" onClick={onBack}>← All uniform orders</button>
                {error ? <div className="campus-orders-error">{error}</div> : <p className="campus-subnote">Loading…</p>}
            </div>
        );
    }

    const live = members.filter((m) => m.status !== "removed");
    const org = orgs.find((o) => o.id === order.org_id) || { name: order.org_name };
    const toggleCutting = () => {
        const on = !order.production_started;
        const msg = on
            ? "Mark cutting as started? From now on every change staff make to a person is flagged here."
            : "Mark cutting as NOT started?";
        if (!window.confirm(msg)) return;
        run(() => call(`/uniform/orders/${orderId}/admin`, { method: "PATCH", body: { production_started: on } }),
            on ? "Cutting marked as started." : "Cutting mark removed.");
    };

    return (
        <div className="campus-orders-page uni-admin">
            <button type="button" className="uni-back" onClick={onBack}>← All uniform orders</button>
            <div className="uni-bar">
                <div>
                    <p className="uni-kicker">{order.order_no}{order.title ? ` · ${order.title}` : ""}</p>
                    <h3 className="uni-h">{order.org_name}</h3>
                    <p className="campus-subnote">
                        {live.length} people · {live.filter((m) => m.status === "done").length} complete ·
                        {" "}{live.filter((m) => !m.bill_id).length} not billed
                        {order.created_by_name ? ` · created by ${order.created_by_name}` : ""}
                    </p>
                </div>
                <div className="uni-actions">
                    <button type="button" className={`uni-btn ${order.production_started ? "on" : ""}`} onClick={toggleCutting} disabled={busy}>
                        {order.production_started ? `✂ Cutting started ${fmtDate(order.production_started_at)}` : "✂ Mark cutting started"}
                    </button>
                    <button type="button" className="campus-orders-refresh" onClick={load} disabled={busy}>{busy ? "Loading…" : "↻ Refresh"}</button>
                </div>
            </div>
            {error && <div className="campus-orders-error">{error}</div>}
            {notice && <div className="uni-notice">{notice}</div>}

            <div className="campus-tabs uni-tabs">
                {TABS.map((t) => (
                    <button key={t.id} type="button" className={`campus-tab ${tab === t.id ? "is-active" : ""}`} onClick={() => setTab(t.id)}>{t.label}</button>
                ))}
            </div>

            {tab === "members" && <MembersTab order={order} members={members} bills={bills} busy={busy} run={run} />}
            {tab === "summary" && <SummaryTab order={order} members={live} />}
            {tab === "bills" && <BillsTab order={order} org={org} members={members} bills={bills} busy={busy} run={run} />}
            {tab === "org" && <OrgTab org={org} busy={busy} run={run} />}
        </div>
    );
}

// ── people ──────────────────────────────────────────────────────────────────

const FILTERS = [
    ["all", "All"], ["pending", "Not complete"], ["done", "Complete"], ["unbilled", "Not billed"],
    ["flagged", "Changed after billing / cutting"], ["removed", "Removed"],
];

function MembersTab({ order, members, bills, busy, run }) {
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [picked, setPicked] = useState({});
    const [openId, setOpenId] = useState(null);
    const [addTo, setAddTo] = useState("");

    const billNo = (id) => bills.find((b) => b.id === id)?.bill_no || "";
    const kitName = (id) => (order.kits || []).find((k) => k.id === id)?.name || "";
    const rows = members.filter((m) => {
        if (filter === "removed") { if (m.status !== "removed") return false; }
        else if (m.status === "removed" && filter !== "flagged") return false;
        if (filter === "pending" && m.status === "done") return false;
        if (filter === "done" && m.status !== "done") return false;
        if (filter === "unbilled" && m.bill_id) return false;
        if (filter === "flagged" && !m.changed_after_production) return false;
        const s = q.trim().toLowerCase();
        return !s || [m.name, m.mobile, m.emp_id, String(m.member_no)].some((v) => String(v || "").toLowerCase().includes(s));
    });
    const pickedIds = Object.keys(picked).filter((k) => picked[k]);
    const pickable = (m) => !m.bill_id && m.status !== "removed";
    const pickedPending = members.filter((m) => pickedIds.includes(m.id) && m.status !== "done").length;

    const confirmPending = () => !pickedPending || window.confirm(
        `${pickedPending} of the selected people are not complete yet (size or fabric missing). Bill them anyway?`);
    const newBill = () => {
        if (!confirmPending()) return;
        run(() => call(`/uniform/orders/${order.id}/bills`, { method: "POST", body: { member_ids: pickedIds } }), "Bill created.")
            .then((done) => done && setPicked({}));
    };
    const addToBill = () => {
        if (!addTo || !confirmPending()) return;
        run(() => call(`/uniform/bills/${addTo}/members`, { method: "POST", body: { add: pickedIds } }), "Added to the bill — the bill is revised.")
            .then((done) => done && setPicked({}));
    };

    return (
        <div className="campus-panel">
            <div className="uni-filter-row">
                {FILTERS.map(([k, l]) => (
                    <button key={k} type="button" className={`campus-drive-chip ${filter === k ? "is-on" : ""}`} onClick={() => setFilter(k)}>{l}</button>
                ))}
                <input className="uni-search" placeholder="Search name, mobile, ID, number…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>

            {pickedIds.length > 0 && (
                <div className="uni-billbar">
                    <b>{pickedIds.length} selected</b>
                    <button type="button" className="campus-orders-refresh" onClick={newBill} disabled={busy}>New bill</button>
                    {bills.length > 0 && (
                        <>
                            <span>or add to</span>
                            <select value={addTo} onChange={(e) => setAddTo(e.target.value)}>
                                <option value="">— pick a bill —</option>
                                {bills.map((b) => <option key={b.id} value={b.id}>{b.bill_no} · {b.member_ids?.length} people · {fmtINR(b.total)}</option>)}
                            </select>
                            <button type="button" className="uni-btn" onClick={addToBill} disabled={busy || !addTo}>Add to bill</button>
                        </>
                    )}
                    <button type="button" className="uni-link" onClick={() => setPicked({})}>Clear</button>
                </div>
            )}

            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr>
                        <th className="c">
                            <input type="checkbox" aria-label="Select all not billed"
                                checked={rows.some(pickable) && rows.filter(pickable).every((m) => picked[m.id])}
                                onChange={(e) => setPicked((p) => ({ ...p, ...Object.fromEntries(rows.filter(pickable).map((m) => [m.id, e.target.checked])) }))} />
                        </th>
                        <th className="c">#</th><th>Name</th><th>Mobile / ID</th><th>Kit</th><th>Items & sizes</th>
                        <th>Measured by</th><th>Status</th><th>Bill</th>
                    </tr></thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={9} className="c">No people here.</td></tr>}
                        {rows.map((m) => (
                            <MemberRow key={m.id} m={m} open={openId === m.id} kit={kitName(m.kit_id)} billNo={billNo(m.bill_id)}
                                picked={!!picked[m.id]} pickable={pickable(m)}
                                onPick={(v) => setPicked((p) => ({ ...p, [m.id]: v }))}
                                onToggle={() => setOpenId(openId === m.id ? null : m.id)}
                                onReview={() => run(() => call(`/uniform/members/${m.id}/review`, { method: "POST" }), "Marked as reviewed.")} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const MODE_LABEL = { size: "Picked size", ai: "AI size", full: "Full measurements" };

function MemberRow({ m, open, kit, billNo, picked, pickable, onPick, onToggle, onReview }) {
    const [full, setFull] = useState(null);
    useEffect(() => {
        if (!open || full) return;
        call(`/uniform/members/${m.id}`).then((d) => setFull(d.member)).catch(() => setFull(m));
    }, [open, full, m]);
    const status = m.status === "removed" ? "Removed" : m.status === "done" ? "Complete" : "Not complete";
    return (
        <>
            <tr className={`uni-click ${m.changed_after_production ? "uni-row-flag" : ""} ${m.status === "removed" ? "uni-row-removed" : ""}`} onClick={onToggle}>
                <td className="c" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" disabled={!pickable} checked={picked} onChange={(e) => onPick(e.target.checked)} />
                </td>
                <td className="c">{m.member_no}</td>
                <td><b>{m.name || "—"}</b>{m.gender === "female" ? " (F)" : ""}</td>
                <td>{[m.mobile, m.emp_id].filter(Boolean).join(" · ") || "—"}</td>
                <td>{kit || "—"}</td>
                <td>{memberItemsText(m) || "—"}</td>
                <td>{MODE_LABEL[m.measure_mode] || "—"}</td>
                <td>
                    <span className={`uni-pill ${m.status === "done" ? "good" : m.status === "removed" ? "" : "warn"}`}>{status}</span>
                    {m.changed_after_production && <span className="uni-pill bad">Changed</span>}
                </td>
                <td>{billNo || "—"}</td>
            </tr>
            {open && (
                <tr className="uni-detail-row">
                    <td colSpan={9}>
                        <MemberDetail m={full || m} onReview={onReview} />
                    </td>
                </tr>
            )}
        </>
    );
}

function MemberDetail({ m, onReview }) {
    const meas = m.measurements || {};
    return (
        <div className="uni-detail">
            {m.changed_after_production && (
                <div className="uni-flagbox">
                    <b>Changed after billing / cutting.</b> Check the bill and the cutting sheet.
                    <ul>
                        {(m.change_log || []).map((c, i) => (
                            <li key={i}>{fmtDate(c.at)} {c.by_name ? `by ${c.by_name}` : ""}: {(c.fields || []).join(", ")}</li>
                        ))}
                    </ul>
                    <button type="button" className="uni-btn" onClick={onReview}>Mark reviewed</button>
                </div>
            )}
            <div className="uni-detail-grid">
                <div>
                    <p className="uni-kicker">Items</p>
                    {(m.active_items || []).map((it) => (
                        <div key={it.id} className="uni-item-line">
                            <b>{it.name}</b>
                            <span>{m[`${it.id}_size`] ? `Size ${m[`${it.id}_size`]}` : ""}{m[`${it.id}_size_source`] === "ai" ? ` (AI, ${m[`${it.id}_size_conf`] || "?"})` : ""}</span>
                            <span>{m[`${it.id}_fabric`] || m[`${it.id}_color`] || ""}{m[`${it.id}_shade`] ? ` · ${m[`${it.id}_shade`]}` : ""}</span>
                        </div>
                    ))}
                    <p className="campus-note">
                        Fit: {m.fit || "regular"}
                        {m.height_cm ? ` · ${m.height_cm} cm` : ""}{m.weight_kg ? ` · ${m.weight_kg} kg` : ""}{m.age_years ? ` · ${m.age_years} yrs` : ""}
                        {m.tape_chest ? ` · tape chest ${m.tape_chest}"` : ""}{m.tape_waist ? ` · tape waist ${m.tape_waist}"` : ""}
                    </p>
                </div>
                {Object.keys(meas).length > 0 && (
                    <div>
                        <p className="uni-kicker">Measurements (inches)</p>
                        <div className="uni-meas">
                            {Object.entries(meas).map(([k, v]) => <span key={k}><small>{k.replace(/_/g, " ")}</small><b>{v}</b></span>)}
                        </div>
                    </div>
                )}
                {(m.front_photo || m.side_photo) && (
                    <div className="uni-photos">
                        {m.front_photo && <img src={m.front_photo} alt="Front" />}
                        {m.side_photo && <img src={m.side_photo} alt="Side" />}
                    </div>
                )}
            </div>
            <p className="campus-note">Added by {m.created_by_name || "—"} · last change by {m.updated_by_name || "—"} {fmtDate(m.updated_at)}</p>
        </div>
    );
}

// ── size & fabric summary ───────────────────────────────────────────────────

export function sizeSummary(members) {
    const byGarment = {};
    const fabrics = {};
    const names = [];
    for (const m of members) {
        for (const it of m.active_items || []) {
            const g = (byGarment[it.type] = byGarment[it.type] || { garment: it.type, sizes: {}, total: 0 });
            const size = runOf(it.type) === "free" ? "Free size" : (m[`${it.id}_size`] || (m.measure_mode === "full" ? "Measured" : "No size"));
            g.sizes[size] = (g.sizes[size] || 0) + 1;
            g.total++;
            const fab = m[`${it.id}_fabric`] || m[`${it.id}_color`] || "—";
            const shade = m[`${it.id}_shade`] || "";
            const fk = `${it.type}|${fab}|${shade}`;
            fabrics[fk] = fabrics[fk] || { garment: it.type, fabric: fab, shade, qty: 0 };
            fabrics[fk].qty++;
        }
        names.push(m);
    }
    const garments = Object.values(byGarment).sort((a, b) => label(a.garment).localeCompare(label(b.garment)));
    for (const g of garments) {
        const order = sizeOrder(g.garment);
        g.columns = Object.keys(g.sizes).sort((a, b) => {
            const ia = order.indexOf(a), ib = order.indexOf(b);
            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
        });
    }
    return { garments, fabrics: Object.values(fabrics).sort((a, b) => label(a.garment).localeCompare(label(b.garment)) || b.qty - a.qty), names };
}

function SummaryTab({ order, members }) {
    const s = useMemo(() => sizeSummary(members), [members]);
    const embroider = (order.kits || []).some((k) => k.name_embroidery);
    return (
        <div className="campus-panel">
            <div className="uni-bar">
                <p className="campus-subnote">Counts every person not removed. "No size" means staff have not finished that person yet.</p>
                <button type="button" className="uni-btn" onClick={() => printSummary(order, s, embroider)}>Print cutting summary</button>
            </div>
            <p className="campus-table-title">Size-wise</p>
            {s.garments.map((g) => (
                <div key={g.garment} className="campus-table-wrap uni-gap">
                    <table className="campus-table">
                        <thead><tr><th>{label(g.garment)}</th>{g.columns.map((c) => <th key={c} className="c">{c}</th>)}<th className="r">Total</th></tr></thead>
                        <tbody><tr>
                            <td>Pieces</td>
                            {g.columns.map((c) => <td key={c} className={`c ${/No size/.test(c) ? "uni-warn-cell" : ""}`}>{g.sizes[c]}</td>)}
                            <td className="r"><b>{g.total}</b></td>
                        </tr></tbody>
                    </table>
                </div>
            ))}
            {!s.garments.length && <p className="campus-note">No items yet.</p>}
            <p className="campus-table-title">Fabric-wise</p>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr><th>Garment</th><th>Fabric / colour</th><th>Shade</th><th className="r">Pieces</th></tr></thead>
                    <tbody>
                        {s.fabrics.map((f, i) => (
                            <tr key={i}><td>{label(f.garment)}</td><td>{f.fabric}</td><td>{f.shade || "—"}</td><td className="r">{f.qty}</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function printSummary(order, s, embroider) {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { alert("Popup blocked — please allow popups."); return; }
    const sizeTables = s.garments.map((g) => `
        <table><thead><tr><th>${esc(label(g.garment))}</th>${g.columns.map((c) => `<th>${esc(c)}</th>`).join("")}<th>Total</th></tr></thead>
        <tbody><tr><td>Pieces</td>${g.columns.map((c) => `<td>${g.sizes[c]}</td>`).join("")}<td><b>${g.total}</b></td></tr></tbody></table>`).join("");
    const fabricRows = s.fabrics.map((f) => `<tr><td>${esc(label(f.garment))}</td><td>${esc(f.fabric)}</td><td>${esc(f.shade || "—")}</td><td>${f.qty}</td></tr>`).join("");
    const people = s.names.map((m) => `<tr><td>${m.member_no}</td><td>${esc(m.name)}</td><td>${esc(memberItemsText(m))}</td>${embroider ? `<td>${esc(m.name)}</td>` : ""}</tr>`).join("");
    w.document.write(`<html><head><meta charset="utf-8"/><title>${esc(order.order_no)} cutting summary</title>
<style>body{font-family:Segoe UI,Arial,sans-serif;font-size:11px;color:#111;margin:18px}h1{font-size:16px;margin:0}h2{font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#555;margin:18px 0 6px}
table{border-collapse:collapse;width:100%;margin-bottom:8px}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left}th{background:#f0f0f0}@page{size:A4;margin:10mm}</style></head><body>
<h1>${esc(order.org_name)} — ${esc(order.order_no)}${order.title ? ` · ${esc(order.title)}` : ""}</h1>
<div>${s.names.length} people · printed ${fmtDate(new Date())}</div>
<h2>Size-wise</h2>${sizeTables}
<h2>Fabric-wise</h2><table><thead><tr><th>Garment</th><th>Fabric / colour</th><th>Shade</th><th>Pieces</th></tr></thead><tbody>${fabricRows}</tbody></table>
<h2>People</h2><table><thead><tr><th>#</th><th>Name</th><th>Items &amp; sizes</th>${embroider ? "<th>Name to embroider</th>" : ""}</tr></thead><tbody>${people}</tbody></table>
<script>window.onload=function(){window.focus();window.print()}<\/script></body></html>`);
    w.document.close();
}

// ── rates & bills ───────────────────────────────────────────────────────────

function BillsTab({ order, org, members, bills, busy, run }) {
    const live = members.filter((m) => m.status !== "removed");
    const garments = useMemo(() => {
        const q = {};
        for (const m of live) for (const it of m.active_items || []) q[it.type] = (q[it.type] || 0) + 1;
        for (const g of Object.keys(order.rates || {})) q[g] = q[g] || 0;
        return Object.entries(q).sort((a, b) => label(a[0]).localeCompare(label(b[0])));
    }, [live, order.rates]);
    const [rates, setRates] = useState(() => ({ ...(order.rates || {}) }));
    const [inclusive, setInclusive] = useState(order.rates_include_gst !== false);
    useEffect(() => { setRates({ ...(order.rates || {}) }); setInclusive(order.rates_include_gst !== false); }, [order]);
    const dirty = JSON.stringify(rates) !== JSON.stringify(order.rates || {}) || inclusive !== (order.rates_include_gst !== false);

    const saveRates = () => run(() => call(`/uniform/orders/${order.id}/admin`, {
        method: "PATCH",
        body: { rates: Object.fromEntries(Object.entries(rates).filter(([, v]) => num(v) > 0).map(([k, v]) => [k, num(v)])), rates_include_gst: inclusive },
    }), "Rates saved. Existing bills keep their old amounts until you press Recalculate on them.");

    const estimate = garments.reduce((a, [g, n]) => a + n * num(rates[g]), 0);
    return (
        <div className="campus-panel">
            <p className="campus-table-title">Rates (per piece)</p>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr><th>Garment</th><th className="c">Pieces in order</th><th>Rate (₹ per piece)</th><th className="r">Amount</th></tr></thead>
                    <tbody>
                        {garments.map(([g, n]) => (
                            <tr key={g}>
                                <td>{label(g)}</td>
                                <td className="c">{n}</td>
                                <td><input className="uni-rate" type="number" min="0" value={rates[g] ?? ""} onChange={(e) => setRates((r) => ({ ...r, [g]: e.target.value }))} /></td>
                                <td className="r">{fmtINR(n * num(rates[g]))}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot><tr><td colSpan={3}><b>Whole order at these rates</b> ({inclusive ? "GST included" : "plus GST"})</td><td className="r"><b>{fmtINR(estimate)}</b></td></tr></tfoot>
                </table>
            </div>
            <div className="uni-bar">
                <label className="uni-check">
                    <input type="checkbox" checked={inclusive} onChange={(e) => setInclusive(e.target.checked)} />
                    Rates include GST (like campus prices). Untick to add GST on top.
                </label>
                <button type="button" className="campus-orders-refresh" onClick={saveRates} disabled={busy || !dirty}>Save rates</button>
            </div>
            <p className="campus-note">GST per piece: 5% up to ₹2,500, 18% above — the same rule as campus invoices.</p>

            <p className="campus-table-title">Bills</p>
            {bills.length === 0 && <p className="campus-subnote">No bills yet. On the People tab, tick the people to bill and press "New bill".</p>}
            {bills.map((b) => <BillCard key={b.id} bill={b} order={order} org={org} members={members} busy={busy} run={run} />)}
        </div>
    );
}

function BillCard({ bill, order, org, members, busy, run }) {
    const [pay, setPay] = useState({ amount: "", mode: "online", date: new Date().toISOString().slice(0, 10), note: "" });
    const [showLog, setShowLog] = useState(false);
    const onBill = members.filter((m) => (bill.member_ids || []).includes(m.id));
    const stale = onBill.some((m) => m.changed_after_production || m.status === "removed");
    const addPayment = () => run(() => call(`/uniform/bills/${bill.id}/payments`, { method: "POST", body: { ...pay, amount: num(pay.amount) } }), "Payment recorded.")
        .then((done) => done && setPay((p) => ({ ...p, amount: "", note: "" })));
    const recalc = () => {
        if (!window.confirm(`Recalculate ${bill.bill_no} from the people on it and the current rates?`)) return;
        run(() => call(`/uniform/bills/${bill.id}/members`, { method: "POST", body: { add: [], remove: [] } }), "Bill recalculated.");
    };
    const removeMember = (m) => {
        if (!window.confirm(`Take ${m.name} off ${bill.bill_no}? They go back to "not billed".`)) return;
        run(() => call(`/uniform/bills/${bill.id}/members`, { method: "POST", body: { add: [], remove: [m.id] } }), `${m.name} taken off the bill.`);
    };
    const removePayment = (p) => {
        if (!window.confirm(`Remove the payment of ${fmtINR(p.amount)} on ${p.date}? It stays in the bill's log.`)) return;
        run(() => call(`/uniform/bills/${bill.id}/payments/${p.id}`, { method: "DELETE" }), "Payment removed.");
    };
    return (
        <div className="uni-bill">
            <div className="uni-bar">
                <div>
                    <h4 className="uni-h4">{bill.bill_no}</h4>
                    <p className="campus-subnote">{fmtDate(bill.created_at)} · {bill.member_ids?.length || 0} people · {bill.pieces} pieces · {bill.rates_include_gst ? "GST included" : "GST added"}</p>
                </div>
                <div className="uni-money">
                    <span>Total <b>{fmtINR(bill.total)}</b></span>
                    <span>Paid <b>{fmtINR(bill.paid)}</b></span>
                    <span className={bill.balance > 0 ? "due" : "ok"}>Balance <b>{fmtINR(bill.balance)}</b></span>
                </div>
            </div>
            {stale && <div className="uni-flagbox">Someone on this bill was changed or removed by staff after billing. Check them on the People tab, then Recalculate or take them off.</div>}
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr><th>Garment</th><th className="c">Qty</th><th className="r">Rate</th><th className="c">GST</th><th className="r">Taxable</th><th className="r">Tax</th><th className="r">Amount</th></tr></thead>
                    <tbody>
                        {(bill.lines || []).map((l) => (
                            <tr key={l.garment}><td>{label(l.garment)}</td><td className="c">{l.qty}</td><td className="r">{fmtINR(l.rate)}</td><td className="c">{l.gst_pct}%</td>
                                <td className="r">{fmtINR(l.taxable)}</td><td className="r">{fmtINR(l.tax)}</td><td className="r">{fmtINR(l.gross)}</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="uni-actions uni-gap-top">
                <button type="button" className="campus-orders-refresh" onClick={() => printBill(bill, order, org, onBill)}>Print invoice</button>
                <button type="button" className="uni-btn" onClick={recalc} disabled={busy}>Recalculate</button>
                <button type="button" className="uni-link" onClick={() => setShowLog((v) => !v)}>{showLog ? "Hide" : "Show"} people & history</button>
            </div>

            <div className="uni-pay">
                <b>Record payment</b>
                <input type="number" min="0" placeholder="Amount ₹" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
                <select value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })}>
                    <option value="online">Online / bank</option><option value="cash">Cash</option><option value="cheque">Cheque</option>
                </select>
                <input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} />
                <input placeholder="Note (UTR, cheque no…)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} />
                <button type="button" className="uni-btn" onClick={addPayment} disabled={busy || num(pay.amount) <= 0}>Add</button>
            </div>
            {(bill.payments || []).length > 0 && (
                <table className="campus-table uni-pay-table">
                    <tbody>
                        {bill.payments.map((p) => (
                            <tr key={p.id}><td>{fmtDate(p.date)}</td><td>{p.mode}</td><td>{p.note}</td><td className="r">{fmtINR(p.amount)}</td>
                                <td className="r"><button type="button" className="uni-link" onClick={() => removePayment(p)}>Remove</button></td></tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showLog && (
                <div className="uni-gap-top">
                    <table className="campus-table">
                        <thead><tr><th className="c">#</th><th>Name</th><th>Items & sizes</th><th /></tr></thead>
                        <tbody>
                            {onBill.map((m) => (
                                <tr key={m.id}><td className="c">{m.member_no}</td><td>{m.name}{m.status === "removed" ? " (removed)" : ""}</td><td>{memberItemsText(m)}</td>
                                    <td className="r"><button type="button" className="uni-link" onClick={() => removeMember(m)}>Take off bill</button></td></tr>
                            ))}
                        </tbody>
                    </table>
                    <ul className="uni-log">
                        {(bill.revisions || []).map((r, i) => (
                            <li key={i}>{fmtDate(r.at)} · {r.action}{r.added ? ` · +${r.added} people` : ""}{r.removed ? ` · −${r.removed} people` : ""}
                                {r.total_before != null ? ` · ${fmtINR(r.total_before)} → ${fmtINR(r.total_after)}` : ""}{r.by_name ? ` · ${r.by_name}` : ""}</li>
                        ))}
                        {(bill.removed_payments || []).map((p) => (
                            <li key={p.id}>{fmtDate(p.removed_at)} · payment of {fmtINR(p.amount)} removed{p.removed_by_name ? ` by ${p.removed_by_name}` : ""}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function numberToWordsIN(n) {
    const value = Math.round(Number(n || 0));
    if (!value) return "Zero Rupees Only";
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
        "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (x) => (x < 20 ? ones[x] : `${tens[Math.floor(x / 10)]}${x % 10 ? ` ${ones[x % 10]}` : ""}`);
    const three = (x) => `${Math.floor(x / 100) ? `${ones[Math.floor(x / 100)]} Hundred${x % 100 ? " " : ""}` : ""}${x % 100 ? two(x % 100) : ""}`;
    let v = value, out = "";
    const cr = Math.floor(v / 1e7); v %= 1e7;
    const lk = Math.floor(v / 1e5); v %= 1e5;
    const th = Math.floor(v / 1e3); v %= 1e3;
    if (cr) out += `${three(cr)} Crore `;
    if (lk) out += `${two(lk)} Lakh `;
    if (th) out += `${two(th)} Thousand `;
    if (v) out += three(v);
    return `${out.trim()} Rupees Only`;
}

// Tax invoice to the organisation, in the campus invoice's look. Amounts come
// from the bill as the server worked them out; this only lays them out.
function printBill(bill, order, org, people) {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { alert("Popup blocked — please allow popups to open the invoice."); return; }
    // The bill keeps the buyer details it was made with; blanks come from the org now.
    const buyer = { ...(org || {}), ...Object.fromEntries(Object.entries(bill.org || {}).filter(([, v]) => v)) };
    const state = buyer.state || FIRM.state;
    const intra = state.toLowerCase() === FIRM.state.toLowerCase();
    const rows = (bill.lines || []).map((l, i) => `<tr><td class="c">${i + 1}</td><td><b>${esc(label(l.garment))}</b></td><td class="c">${HSN_GARMENT}</td>
        <td class="c">${l.qty}</td><td class="r">${fmtINR(l.rate)}</td><td class="r">${fmtINR(l.taxable)}</td><td class="c">${l.gst_pct}%</td><td class="r">${fmtINR(l.gross)}</td></tr>`).join("");
    const rates = [...new Set((bill.lines || []).map((l) => l.gst_pct))];
    const byRate = rates.map((r) => {
        const ls = bill.lines.filter((l) => l.gst_pct === r);
        return { r, taxable: ls.reduce((a, l) => a + l.taxable, 0), tax: ls.reduce((a, l) => a + l.tax, 0) };
    });
    const taxRows = byRate.map((t) => (intra
        ? `<div class="row"><span>CGST @ ${t.r / 2}%</span><span>${fmtINR(t.tax / 2)}</span></div><div class="row"><span>SGST @ ${t.r / 2}%</span><span>${fmtINR(t.tax / 2)}</span></div>`
        : `<div class="row"><span>IGST @ ${t.r}%</span><span>${fmtINR(t.tax)}</span></div>`)).join("");
    const annex = people.map((m) => `<tr><td class="c">${m.member_no}</td><td>${esc(m.name)}</td><td>${esc(m.emp_id || m.mobile || "")}</td><td>${esc(memberItemsText(m))}</td></tr>`).join("");
    w.document.write(`<html><head><meta charset="utf-8"/><title>${esc(bill.bill_no)}</title><style>
*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1410;margin:0;padding:10px;background:#f4f1eb;font-size:10px}
.inv{width:190mm;margin:0 auto;background:#fff;border:1px solid #e2d7c6;border-radius:10px;overflow:hidden}
.head{display:flex;justify-content:space-between;gap:16px;padding:10px 16px 8px;border-bottom:2px solid #17110a}
.head img{height:34px}.firm{margin-top:6px;font-size:9px;line-height:1.4;color:#4a3f33}.firm .legal{font-size:12px;font-weight:800;color:#17110a}
.firm .gst{font-weight:700;color:#9d7130}.title{text-align:right}.title h1{margin:0;font-size:17px;letter-spacing:.14em;color:#9d7130;text-transform:uppercase}
.meta{margin-top:6px;font-size:9.5px;line-height:1.5}.parties{display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #eadfce}
.party{padding:8px 16px;font-size:9.5px;line-height:1.45;color:#4a3f33}.party:first-child{border-right:1px solid #eadfce}
.lbl{font-size:8.5px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#9d7130;margin-bottom:3px}.pname{font-size:12px;font-weight:800;color:#17110a}
table{width:100%;border-collapse:collapse;font-size:9.5px}thead th{background:#17110a;color:#f3e7cf;font-size:8.5px;text-transform:uppercase;padding:6px 7px;text-align:left}
tbody td{padding:5px 7px;border-bottom:1px solid #efe7d8}.c{text-align:center}.r{text-align:right}
.bottom{display:grid;grid-template-columns:1.1fr .9fr;border-top:2px solid #17110a}.words{padding:8px 16px;border-right:1px solid #eadfce}.words .amt{font-weight:800;font-size:11px}
.tot{padding:7px 16px}.row{display:flex;justify-content:space-between;padding:2px 0}.net{display:flex;justify-content:space-between;margin-top:4px;padding:7px 12px;background:#17110a;color:#fff;border-radius:8px;font-weight:800;font-size:14px}
.due{display:flex;justify-content:space-between;color:#9d7130;font-weight:800;margin-top:4px}.foot{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid #eadfce}
.foot>div{padding:6px 16px;font-size:8.5px}.sign{text-align:right}.sign img{height:30px}.terms{padding:5px 16px 8px;font-size:7.5px;color:#8a7a64;border-top:1px solid #eadfce}
.annex{page-break-before:always;padding:12px 16px}.annex h2{font-size:12px;margin:0 0 8px}
@media print{body{background:#fff;padding:0}.inv{border:none;width:100%}@page{size:A4;margin:6mm}}
</style></head><body><div class="inv">
<div class="head"><div><img src="${abs(logoUrl)}" alt="MAVIINCI"/><div class="firm"><div class="legal">${FIRM.legalName}</div><div>${FIRM.address}, ${FIRM.district}</div>
<div>${FIRM.website} | ${FIRM.email}</div><div class="gst">GSTIN: ${FIRM.gstin} &nbsp; State: ${FIRM.state} (${FIRM.stateCode})</div></div></div>
<div class="title"><h1>Tax Invoice</h1><div class="meta"><div><b>Invoice No:</b> ${esc(bill.bill_no)}</div><div><b>Date:</b> ${fmtDate(bill.created_at)}</div>
<div><b>Order:</b> ${esc(order.order_no)}${order.title ? ` · ${esc(order.title)}` : ""}</div><div><b>Place of Supply:</b> ${esc(state)}</div></div></div></div>
<div class="parties"><div class="party"><div class="lbl">Bill To</div><div class="pname">${esc(buyer.name || order.org_name)}</div>
${buyer.address ? `<div>${esc(buyer.address)}</div>` : ""}${buyer.city ? `<div>${esc(buyer.city)}${buyer.state ? `, ${esc(buyer.state)}` : ""}</div>` : ""}
${buyer.gstin ? `<div><b>GSTIN:</b> ${esc(buyer.gstin)}</div>` : ""}${buyer.contact_name || buyer.contact_mobile ? `<div>Contact: ${esc([buyer.contact_name, buyer.contact_mobile].filter(Boolean).join(" · "))}</div>` : ""}</div>
<div class="party"><div class="lbl">Order Info</div><div>${bill.member_ids?.length || 0} people · ${bill.pieces} pieces</div><div>Uniforms stitched to each person's size</div></div></div>
<table><thead><tr><th class="c">#</th><th>Description of Goods</th><th class="c">HSN</th><th class="c">Qty</th><th class="r">Rate</th><th class="r">Taxable</th><th class="c">GST%</th><th class="r">Amount</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="bottom"><div class="words"><div class="lbl">Amount Chargeable (in words)</div><div class="amt">${numberToWordsIN(bill.total)}</div>
<div style="margin-top:6px">Rates ${bill.rates_include_gst ? "are inclusive of" : "are exclusive of"} GST. Person-wise list attached.</div></div>
<div class="tot"><div class="row"><span>Taxable Value</span><b>${fmtINR(bill.taxable)}</b></div>${taxRows}
${Math.abs(bill.round_off || 0) >= 0.01 ? `<div class="row"><span>Round Off</span><span>${bill.round_off > 0 ? "+" : "−"} ${fmtINR(Math.abs(bill.round_off))}</span></div>` : ""}
<div class="net"><span>Total</span><span>${fmtINR(bill.total)}</span></div>
${bill.paid > 0 ? `<div class="due"><span>Paid</span><span>− ${fmtINR(bill.paid)}</span></div><div class="due"><span>Balance Due</span><span>${fmtINR(bill.balance)}</span></div>` : ""}</div></div>
<div class="foot"><div><div class="lbl">Notes</div><div>Thank you for choosing ${FIRM.brand}.</div><div>Support: ${FIRM.founderMobile} | ${FIRM.coFounderMobile}</div></div>
<div class="sign"><div><b>For ${FIRM.legalName}</b></div><img src="${abs(signUrl)}" alt=""/><div>Authorised Signatory</div></div></div>
<div class="terms"><b>Terms &amp; Conditions:</b> Goods are made to order and non-returnable. This is a computer-generated invoice. Subject to Durg (Chhattisgarh) jurisdiction.</div>
<div class="annex"><h2>Annexure — people on ${esc(bill.bill_no)}</h2><table><thead><tr><th class="c">#</th><th>Name</th><th>Employee ID / Mobile</th><th>Items &amp; sizes</th></tr></thead><tbody>${annex}</tbody></table></div>
</div><script>window.onload=function(){window.focus();window.print()}<\/script></body></html>`);
    w.document.close();
}

// ── organisation ────────────────────────────────────────────────────────────

function OrgTab({ org, busy, run }) {
    const [f, setF] = useState(org);
    useEffect(() => setF(org), [org]);
    const field = (k, l, wide) => (
        <label className={`uni-field ${wide ? "wide" : ""}`}><span>{l}</span>
            <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
    );
    if (!org?.id) return <p className="campus-subnote">Organisation not found.</p>;
    return (
        <div className="campus-panel">
            <p className="campus-subnote">These details go on new bills. A bill already made keeps the details it was made with, and only fills blanks (like a missing state or GSTIN) from here.</p>
            <div className="uni-form">
                {field("name", "Name", true)}
                {field("gstin", "GSTIN")}
                <label className="uni-field"><span>State (place of supply)</span>
                    <select value={f.state || ""} onChange={(e) => setF({ ...f, state: e.target.value })}>
                        <option value="">— pick —</option>
                        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                {field("address", "Address", true)}
                {field("city", "City")}
                {field("contact_name", "Contact person")}
                {field("contact_mobile", "Contact mobile")}
            </div>
            <button type="button" className="campus-orders-refresh" disabled={busy}
                onClick={() => run(() => call(`/uniform/orgs/${org.id}`, { method: "PATCH", body: f }), "Organisation saved.")}>Save</button>
        </div>
    );
}
